/**
 * CSK Demo Hub — top-level shell.
 *
 * Reads `window.location.pathname` to pick the active route. `/` renders
 * the launcher; `/<slug>` renders the matching demo's `App` inside a
 * `DemoFrame`. Unknown slugs fall through to the launcher unchanged —
 * since the launcher is the full catalog, "not found" is visually
 * indistinguishable from the home page.
 *
 * Navigation is soft (Phase 3): a global click interceptor catches plain
 * left-clicks on same-origin path anchors (`<a href="/foo">`), rewrites
 * the URL via `history.pushState`, and re-renders without a full document
 * reload. The browser back/forward buttons drive the same state change
 * through the `popstate` listener already present here. To preserve the
 * Phase 0 isolation guarantee — each demo gets a fresh React subtree on
 * activation — the demo component is keyed on its slug, so React unmounts
 * the previous demo and mounts the next one rather than reusing state.
 * Cross-demo modifiers (ctrl/cmd-click, target=_blank, download links,
 * external URLs) all fall through to the browser unchanged.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useEffect, useState } from "react";
import { demoBySlug } from "@csk/demo-ui/catalog";
import { HubLauncher } from "./HubLauncher.js";
import { DemoFrame } from "./DemoFrame.js";
import { routeBySlug, type HubRoute } from "./route-table.js";

function currentSlug(): string {
  const path = window.location.pathname.replace(/^\/+|\/+$/g, "");
  return path;
}

function demoTitle(slug: string): string {
  return demoBySlug(slug)?.title ?? slug;
}

/**
 * Should a given click be soft-navigated? Returns the destination URL when
 * yes; returns `null` when the click should fall through to the browser
 * (modifier-key open-in-new-tab, target attribute, external origin, file
 * download, missing href, …).
 */
function softNavTarget(event: MouseEvent): URL | null {
  if (event.defaultPrevented) return null;
  if (event.button !== 0) return null;
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
    return null;
  }
  // `event.target` is typed as `EventTarget | null` and can be a `Text`
  // node when the click lands on text inside the anchor — `Text` has no
  // `closest()`, so guard the cast rather than assume `Element`.
  const target = event.target instanceof Element ? event.target : null;
  const anchor = target?.closest("a");
  if (!anchor) return null;
  if (anchor.hasAttribute("download")) return null;
  const anchorTarget = anchor.getAttribute("target");
  if (anchorTarget && anchorTarget !== "_self") return null;
  const href = anchor.getAttribute("href");
  if (!href) return null;
  // Skip protocol-relative, mailto:, tel:, javascript:, plain hash links
  // (those are intra-page anchors handled by the browser). We only steer
  // path-rooted, same-origin links.
  if (!href.startsWith("/") || href.startsWith("//")) return null;
  const url = new URL(href, window.location.origin);
  if (url.origin !== window.location.origin) return null;
  return url;
}

export function App(): React.JSX.Element {
  const [slug, setSlug] = useState<string>(() => currentSlug());

  const navigate = useCallback((url: URL): void => {
    const target = url.pathname + url.search + url.hash;
    const current =
      window.location.pathname + window.location.search + window.location.hash;
    if (target === current) return;
    window.history.pushState(null, "", target);
    setSlug(currentSlug());
  }, []);

  useEffect(() => {
    function onPopState(): void {
      setSlug(currentSlug());
    }
    function onClick(event: MouseEvent): void {
      const url = softNavTarget(event);
      if (!url) return;
      event.preventDefault();
      navigate(url);
    }
    window.addEventListener("popstate", onPopState);
    document.addEventListener("click", onClick);
    return (): void => {
      window.removeEventListener("popstate", onPopState);
      document.removeEventListener("click", onClick);
    };
  }, [navigate]);

  if (slug === "") {
    return <HubLauncher />;
  }

  const route: HubRoute | undefined = routeBySlug(slug);
  if (!route) {
    return <HubLauncher />;
  }

  return (
    <DemoFrame
      key={route.slug}
      slug={route.slug}
      title={demoTitle(route.slug)}
      component={route.component}
    />
  );
}
